import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import Thumbtack from './Thumbtack';
import PaperScrap from './PaperScrap';
import InfoModal from './InfoModal';
import { triggerVoidCollapse } from '../../utils/voidCollapse';
import './PinnedLetters.css';

export interface PinnedLettersProps {
  phrase?: string;
  className?: string;
  onTriggerInfo?: () => void;
  onTriggerX?: () => void;
}

interface LetterItem {
  id: string;
  char: string;
  lineIndex: number;
  wordIndex: number;
  charIndex: number;
  globalIndex: number;
  initialRotation: number;
}

export const PinnedLetters: React.FC<PinnedLettersProps> = ({
  phrase = 'iNTERACTIVE\nART EXHIBITION',
  className = '',
  onTriggerInfo,
  onTriggerX
}) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const lastPointerRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const velocityRef = useRef<{ vx: number; vy: number; speed: number }>({ vx: 0, vy: 0, speed: 0 });

  // High-accuracy aerodynamic wind wake tracker: calculates pointer/finger delta & speed
  useEffect(() => {
    let decayTimer: ReturnType<typeof setTimeout> | null = null;

    const handlePointerUpdate = (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      // Proximity zone: within 220px of the board boundary
      const inProximity =
        clientX >= rect.left - 220 &&
        clientX <= rect.right + 220 &&
        clientY >= rect.top - 220 &&
        clientY <= rect.bottom + 220;

      const now = performance.now();
      if (!lastPointerRef.current) {
        lastPointerRef.current = { x: clientX, y: clientY, time: now };
        return;
      }

      const dt = (now - lastPointerRef.current.time) / 1000;
      if (dt < 0.0005) return; // Prevent division by zero; preserves high-rate (1000Hz) events

      const dx = clientX - lastPointerRef.current.x;
      const dy = clientY - lastPointerRef.current.y;
      const rawVx = dx / dt;
      const rawVy = dy / dt;
      const rawSpeed = Math.hypot(rawVx, rawVy);

      // Fast, responsive EMA filter (alpha = 0.85) captures rapid flicks immediately
      const alpha = 0.85;
      const vx = alpha * rawVx + (1 - alpha) * velocityRef.current.vx;
      const vy = alpha * rawVy + (1 - alpha) * velocityRef.current.vy;
      const speed = alpha * rawSpeed + (1 - alpha) * velocityRef.current.speed;

      velocityRef.current = { vx, vy, speed };
      lastPointerRef.current = { x: clientX, y: clientY, time: now };

      if (!inProximity) return;

      // Dispatch wind wake if pointer is actively moving towards or above threshold
      if (speed >= 120 && !(window as any).__SCHZ_COLLAPSED__) {
        window.dispatchEvent(
          new CustomEvent('schz-paper-wind', {
            detail: { x: clientX, y: clientY, vx, vy, speed }
          })
        );
      }

      if (decayTimer) clearTimeout(decayTimer);
      decayTimer = setTimeout(() => {
        velocityRef.current = { vx: 0, vy: 0, speed: 0 };
        lastPointerRef.current = null;
      }, 110);
    };

    const onPointerMove = (e: PointerEvent) => {
      handlePointerUpdate(e.clientX, e.clientY);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        handlePointerUpdate(touch.clientX, touch.clientY);
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('touchmove', onTouchMove);
      if (decayTimer) clearTimeout(decayTimer);
    };
  }, []);

  const lines = useMemo(() => {
    let globalCounter = 0;
    const rawLines = phrase.split('\n');
    return rawLines.map((line, lineIndex) => {
      const words = line.trim().split(/\s+/).filter(Boolean).map((word, wordIndex) => {
        const letters: LetterItem[] = word.split('').map((char, charIndex) => {
          const seed = (lineIndex * 37 + wordIndex * 13 + charIndex * 7 + char.charCodeAt(0)) % 100;
          const initialRotation = (seed / 100) * 7 - 3.5;
          const item: LetterItem = {
            id: `letter-${lineIndex}-${wordIndex}-${charIndex}-${char}`,
            char,
            lineIndex,
            wordIndex,
            charIndex,
            globalIndex: globalCounter++,
            initialRotation
          };
          return item;
        });
        return { word, wordIndex, letters };
      });
      return { lineIndex, words };
    });
  }, [phrase]);

  const handleTriggerInfo = useCallback(() => {
    setIsInfoOpen(true);
    if (onTriggerInfo) onTriggerInfo();
  }, [onTriggerInfo]);

  const handleTriggerX = useCallback(() => {
    if ((window as any).__SCHZ_COLLAPSED__) return;
    if (onTriggerX) onTriggerX();
    triggerVoidCollapse();
  }, [onTriggerX]);

  // Support pressing 'x' or 'X' on keyboard as well as clicking the pinned letter
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'x' || e.key === 'X') && !isInfoOpen && !(window as any).__SCHZ_COLLAPSED__) {
        handleTriggerX();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleTriggerX, isInfoOpen]);

  return (
    <div
      ref={containerRef}
      className={`pinned-letters-container ${className}`}
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        margin: '1.25rem 0',
        userSelect: 'none'
      }}
    >
      <h1 className="sr-only">
        {phrase.replace(/\n/g, ' ')}
      </h1>

      <div
        className="letter-board"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 'clamp(0.6rem, 1.4vw, 1.2rem)',
          maxWidth: '100%',
          padding: '0.5rem 1rem'
        }}
      >
        {lines.map((line) => (
          <div
            key={`line-${line.lineIndex}`}
            className={`letter-line letter-line-${line.lineIndex + 1}`}
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 'clamp(0.8rem, 2vw, 2rem)'
            }}
          >
            {line.words.map((w) => (
              <div
                key={`word-${line.lineIndex}-${w.wordIndex}`}
                className="letter-word"
                style={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 'clamp(4px, 0.8vw, 8px)'
                }}
              >
                {w.letters.map((item) => (
                  <div
                    key={item.id}
                    className="letter-wrapper"
                    style={{
                      position: 'relative',
                      width: 'clamp(26px, 4.2vw, 48px)',
                      height: 'clamp(36px, 5.8vw, 64px)',
                      display: 'inline-block'
                    }}
                  >
                    <PaperScrap
                      char={item.char}
                      wordIndex={item.wordIndex}
                      charIndex={item.charIndex}
                      letterIndex={item.globalIndex}
                      initialRotation={item.initialRotation}
                      onTriggerInfo={handleTriggerInfo}
                      onTriggerX={handleTriggerX}
                    />

                    <Thumbtack pinIndex={item.globalIndex} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* [i] Info Modal Window Component (customizable in InfoModal.tsx) */}
      <InfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
    </div>
  );
};

export default PinnedLetters;
